import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import {
  WorkspaceEngine,
  WorkspaceEngineStatus,
} from "services/engines/engine.types";
import { forceStopEngineMutation } from "services/engines/stopEngine";
import { useStartWorkspaceEngine } from "services/engines/useStartWorkspaceEngine";
import { useStopWorkspaceEngine } from "services/engines/useStopWorkspaceEngine";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import { useAccessManager } from "components/App/accessManager";
import { EngineItemInfo } from "components/composite/ContextSelector/components/EngineItemInfo/EngineItemInfo";
import { EngineOperateTooltipTitle } from "components/composite/ContextSelector/components/tooltipTitles";

import { Chip } from "./Chip";
import { TextChip, TextChipVariants } from "./TextChip";

import styles from "./styles.module.scss";

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  engine: WorkspaceEngine;
  inactive?: boolean;
  inline?: boolean;
  inMenu?: boolean;
  size?: "small" | "regular";
};

const RunningChip = (props: Props) => {
  const { engine, inactive, size = "regular", inline, inMenu } = props;
  const { mutate, isLoading } = useStopWorkspaceEngine();
  const { mutate: stopByForce, isLoading: isStoppingEngineByForce } =
    useStopWorkspaceEngine(forceStopEngineMutation);
  const { isAllowedTo } = useAccessManager();
  const { t } = useTranslation();

  const canOperateEngine =
    engine.engineName !== "system"
      ? isAllowedTo(
          PrivilegeObjectType.engine,
          engine.engineName,
          SqlRbacAction.OPERATE,
          {
            owner: engine.engineOwner,
          }
        )
      : true;
  const items = [
    {
      title: t("workspace.engine_chip.stop"),
      action: () => {
        if (!isLoading && !isStoppingEngineByForce) {
          mutate(engine.engineName);
        }
      },
      testId: "engine-stop-item",
    },
    {
      title: t("workspace.engine_chip.force_stop"),
      action: () => {
        if (!isLoading && !isStoppingEngineByForce) {
          stopByForce(engine.engineName);
        }
      },
      testId: "engine-force-stop-item",
    },
  ];

  return canOperateEngine ? (
    <Chip
      testId="stop-engine-chip"
      className={styles.primary}
      title={t("workspace.engine_chip.stop")}
      items={items}
      inline={inline}
      inMenu={inMenu}
      inactive={inactive}
      size={size}
    />
  ) : (
    <TextChip
      testId="engine-running-no-permissions-text-chip"
      text={t("engine.status.running")}
      variant={TextChipVariants.secondary}
      icon={<EngineItemInfo title={<EngineOperateTooltipTitle />} />}
    />
  );
};

const StartingChip = (props: Props) => {
  const { engine, inactive, size = "regular", inline, inMenu } = props;
  const { isLoading } = useStopWorkspaceEngine();
  const { mutate: stopByForce, isLoading: isStoppingEngineByForce } =
    useStopWorkspaceEngine(forceStopEngineMutation);
  const { isAllowedTo } = useAccessManager();
  const { t } = useTranslation();
  const enabledGcpAccount = useEnableGcpAccount();

  const canOperateEngine =
    engine.engineName !== "system"
      ? isAllowedTo(
          PrivilegeObjectType.engine,
          engine.engineName,
          SqlRbacAction.OPERATE,
          {
            owner: engine.engineOwner,
          }
        )
      : true;
  const items = [
    {
      title: t("workspace.engine_chip.force_stop"),
      action: () => {
        if (!isLoading && !isStoppingEngineByForce) {
          stopByForce(engine.engineName);
        }
      },
      testId: "engine-force-stop-item",
    },
  ];
  return canOperateEngine && !enabledGcpAccount ? (
    <Chip
      testId="stop-engine-chip"
      className={styles.primary}
      title={t("workspace.engine_chip.starting")}
      items={items}
      inline={inline}
      inMenu={inMenu}
      inactive={inactive}
      size={size}
    />
  ) : (
    <TextChip
      testId="engine-starting-no-permissions-text-chip"
      text={t("engine.status.starting")}
      variant={TextChipVariants.secondary}
      icon={
        !enabledGcpAccount ? (
          <EngineItemInfo title={<EngineOperateTooltipTitle />} />
        ) : undefined
      }
    />
  );
};

const ResizingChip = () => {
  const { t } = useTranslation();

  return (
    <TextChip
      text={t("engine.status.resizing")}
      testId="engine-resizing-text-chip"
    />
  );
};

const StoppingChip = () => {
  const { t } = useTranslation();

  return (
    <TextChip
      text={t("engine.status.stopping")}
      testId="engine-stopping-text-chip"
    />
  );
};

const DrainingChip = () => {
  const { t } = useTranslation();

  return (
    <TextChip
      text={t("engine.status.draining")}
      testId="engine-draining-text-chip"
    />
  );
};

const StoppedChip = (props: Props) => {
  const { engine, inactive, size = "regular", inline, inMenu } = props;
  const { mutate } = useStartWorkspaceEngine();
  const { isAllowedTo } = useAccessManager();
  const { t } = useTranslation();
  const enabledGcpAccount = useEnableGcpAccount();

  const canOperateEngine = isAllowedTo(
    PrivilegeObjectType.engine,
    engine.engineName,
    SqlRbacAction.OPERATE,
    { owner: engine.engineOwner }
  );

  return canOperateEngine && !enabledGcpAccount ? (
    <Chip
      className={styles.secondary}
      title={t("workspace.engine_chip.start")}
      items={[]}
      handleClick={() => {
        mutate(engine.engineName);
      }}
      testId="engine-start"
      inactive={inactive}
      inline={inline}
      inMenu={inMenu}
      size={size}
    />
  ) : (
    <TextChip
      testId="engine-stopped-no-permissions-text-chip"
      text={t("engine.status.stopped")}
      variant={TextChipVariants.secondary}
      icon={
        !enabledGcpAccount ? (
          <EngineItemInfo title={<EngineOperateTooltipTitle />} />
        ) : undefined
      }
    />
  );
};

export const chipMap = {
  [WorkspaceEngineStatus.STOPPING]: StoppingChip,
  [WorkspaceEngineStatus.DRAINING]: DrainingChip,
  [WorkspaceEngineStatus.STOPPED]: StoppedChip,

  [WorkspaceEngineStatus.STARTING]: StartingChip,
  [WorkspaceEngineStatus.RUNNING]: RunningChip,

  [WorkspaceEngineStatus.RESIZING]: ResizingChip,
};
